import { createContext, useContext, useEffect, useRef, useState } from "react";
import logo from "../../assets/images/logo-192.png";
import DownloadPageMock from "../landing/DownloadPageMock";
import {
  Stars,
  IconArrow,
  IconMenu,
  IconClose,
  IconCheck,
  IconDownload,
  IconBag,
} from "../landing/Icons";
import {
  APP_STORE_URL,
  APP_STORE_REVIEWS_URL,
  TRY_URL,
  RATING,
  NAV,
  REVIEWS,
  FAQ,
  DPA_URL,
  PRIVACY_URL,
  PUMPER_URL,
  TERMS_URL,
  DEMO_PALETTES,
} from "../site/siteData";
import { useSiteTheme, ThemeToggle, ThemeIcon } from "../site/SiteTheme";
import { FiKey, FiExpiry, FiStamp, FiLock, FiLanguages, FiVariants, FiEmail, FiCloud } from "./FeatureIcons";
import "../../styles/landing.css";
import "../../styles/site-cream.css";

/*
 * Pendora marketing site, cream edition.
 * Sections: hero, a statement that inks in as you read, how it works (a
 * swipeable gallery), what you can sell (image slider), features, try-it
 * teaser, reviews, FAQ, closing. Scrolling is the browser's own; effects
 * follow it and stand still under prefers-reduced-motion.
 */

const TICKER = ["Ebooks", "Sample packs", "Online courses", "License keys", "Digital art", "Planners", "Software", "Audio guides", "Templates", "Sheet music"];

/*
 * Two themes share this page: "cream" (the light editorial site) and "night"
 * (the dark site). Each has its own photographs; everything else is styled
 * by the .pc--night overrides in site-cream.css.
 */
const ART = {
  cream: {
    hero: "/site-cream/hero.webp",
    closing: "/site-cream/closing.webp",
    ebook: "/site-cream/cat-ebook.webp",
    audio: "/site-cream/cat-audio.webp",
    video: "/site-cream/cat-video.webp",
    keys: "/site-cream/cat-keys.webp",
    canvas: "#f3ede2",
  },
  night: {
    hero: "/site/night-hero.webp",
    closing: "/site/night-closing.webp",
    ebook: "/site/cat-ebook.webp",
    audio: "/site/cat-audio.webp",
    video: "/site/cat-video.webp",
    keys: "/site/cat-keys.webp",
    canvas: "#07061c",
  },
};

const ThemeContext = createContext("cream");

/** Phones get the 640px copy; larger screens the full-resolution file. */
const srcSet = (url) => `${url.replace(/\.webp$/, "-640.webp")} 640w, ${url} 1254w`;
const useArt = () => ART[useContext(ThemeContext)];

const STEPS = [
  {
    title: "The order is paid.",
    text: "Pendora marks it fulfilled and the thank-you page shows a download button straight away, before your customer opens their inbox.",
    notes: ["Thank-you page", "Order status page", "No theme code"],
  },
  {
    title: "The email arrives.",
    text: "Files and license keys land in the inbox, in an email you write yourself, sent in your store's name.",
    notes: ["Your subject and words", "Keys included", "Resend any time"],
  },
  {
    title: "The file is theirs.",
    text: "A download page in your colours and their language, with limits and expiry explained clearly instead of a broken link.",
    notes: ["Your colours", "16 languages", "Limits and expiry"],
  },
];

const SELL = [
  {
    img: "ebook",
    title: "Ebooks and PDFs",
    text: "Guides, planners and novels, stamped with the buyer's email and locked with a password if you like.",
    ext: "PDF · EPUB · MOBI",
  },
  {
    img: "audio",
    title: "Music and sound",
    text: "Albums, beats, sample packs and audio guides, as single files or one archive.",
    ext: "MP3 · WAV · FLAC · ZIP",
  },
  {
    img: "video",
    title: "Courses and film",
    text: "Lessons and tutorials, hosted with us or linked from wherever you already keep them.",
    ext: "MP4 · MOV · Links",
  },
  {
    img: "keys",
    title: "Software and keys",
    text: "Installers with a license key in the same email. Every order takes the next key.",
    ext: "ZIP · DMG · Keys",
  },
];

const FEATURES = [
  { Icon: FiKey, title: "License keys", text: "Each paid order takes the next key from your list." },
  { Icon: FiExpiry, title: "Limits and expiry", text: "Cap downloads and let links expire after set hours." },
  { Icon: FiStamp, title: "Stamped PDFs", text: "Every copy carries the buyer's email and order." },
  { Icon: FiLock, title: "Password protection", text: "Lock PDFs with a password sent alongside them." },
  { Icon: FiLanguages, title: "Sixteen languages", text: "The download page speaks your customer's language." },
  { Icon: FiVariants, title: "Per-variant files", text: "Each product option delivers exactly the right file." },
  { Icon: FiEmail, title: "Your email, your words", text: "Write the subject and body your customers read." },
  { Icon: FiCloud, title: "Fast, secure hosting", text: "Signed links from a global CDN, or your own links." },
];


const TRY_LOCALES = ["en", "fr", "de", "ja", "es", "it"];

/* ================================================================ hooks */

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

/** Adds .is-in to [data-reveal] nodes; those already on screen go at once. */
function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const nodes = root.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );
    const raf = requestAnimationFrame(() => {
      nodes.forEach((n) => {
        if (n.getBoundingClientRect().top < window.innerHeight * 0.92) n.classList.add("is-in");
        else io.observe(n);
      });
    });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [rootRef]);
}

/**
 * Scroll-driven effects on top of the browser's own scrolling (nothing is
 * smoothed or hijacked, so trackpads feel exactly like the rest of the OS).
 * One rAF-throttled scroll listener updates:
 *  - [data-scroll]: --p, 0 entering the screen to 1 at its upper third;
 *  - [data-words]: --w on each word, so a statement inks in as you read it;
 *  - the ticker's speed, which follows scroll velocity.
 */
function useScrollFx(rootRef) {
  const reduced = useReducedMotion();
  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return undefined;

    const tracked = [...root.querySelectorAll("[data-scroll]")];
    const statements = [...root.querySelectorAll("[data-words]")].map((el) => ({
      el,
      words: [...el.querySelectorAll(".pc-word")],
    }));
    const threads = [...root.querySelectorAll("[data-thread]")];
    const devices = [...root.querySelectorAll("[data-device]")];
    const ticker = root.querySelector(".pc-ticker__row")?.getAnimations?.()[0];
    let lastY = window.scrollY;
    let velocity = 0;
    let queued = false;
    let settle = 0;

    const update = () => {
      queued = false;
      const vh = window.innerHeight;
      tracked.forEach((el) => {
        const top = el.getBoundingClientRect().top;
        el.style.setProperty("--p", Math.min(1, Math.max(0, (vh - top) / (vh * 0.65))).toFixed(3));
      });
      statements.forEach(({ el, words }) => {
        const r = el.getBoundingClientRect();
        // Fully inked once the statement's top reaches 30% of the screen.
        const t = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (vh * 0.55 + r.height * 0.35)));
        const lit = t * words.length;
        words.forEach((w, n) => w.style.setProperty("--w", Math.min(1, Math.max(0, lit - n)).toFixed(2)));
      });
      // The device lies back at first and settles flat as it reaches the middle.
      devices.forEach((el) => {
        const r = el.getBoundingClientRect();
        const t = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.75)));
        el.style.setProperty("--dv", t.toFixed(4));
      });

      // The delivery: the thread draws itself around the statement with the
      // file riding its tip (phase 1); then the file drops into the tablet
      // screen (phase 2). On arrival the download page assembles itself.
      const thread = threads[0];
      const device = devices[0];
      const token = root.querySelector(".pc-token");
      const screen = root.querySelector(".pc-device__screen");
      const path = thread?.querySelector(".pc-thread path");
      if (thread && token && path && screen && device) {
        const r = thread.getBoundingClientRect();
        const scrollY = window.scrollY;
        const sr = screen.getBoundingClientRect();
        // Wide screens: the file rides the thread around the statement first.
        // Narrower screens: no thread; the file starts under the statement.
        const wide = window.innerWidth > 1100;
        const phase1End = wide
          ? r.top + scrollY - vh * 0.85 + r.height + vh * 0.2
          : r.bottom + scrollY - vh * 0.75;
        const phase2End = Math.max(phase1End + 1, sr.top + scrollY - vh * 0.42);
        const t = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.2)));
        const u = Math.min(1, Math.max(0, (scrollY - phase1End) / (phase2End - phase1End)));
        thread.style.setProperty("--draw", t.toFixed(4));
        thread.style.setProperty("--drift", (-r.top).toFixed(1));

        const len = path.getTotalLength();
        const sx = r.width / 1440;
        const sy = r.height / 760;
        let x;
        let y;
        let angle = 0;
        let scale = 1;
        if (wide && u <= 0) {
          const at = Math.max(0.0001, t) * len;
          const p = path.getPointAtLength(at);
          const q = path.getPointAtLength(Math.min(len, at + 6));
          x = r.left + p.x * sx;
          y = r.top + p.y * sy;
          angle = ((Math.atan2((q.y - p.y) * sy, (q.x - p.x) * sx) * 180) / Math.PI) * 0.22;
        } else {
          let ex;
          let ey;
          if (wide) {
            const end = path.getPointAtLength(len);
            ex = r.left + end.x * sx;
            ey = r.top + end.y * sy;
          } else {
            ex = r.left + r.width / 2;
            ey = r.bottom - 40;
          }
          const tx = sr.left + sr.width / 2;
          const ty = sr.top + Math.min(150, sr.height * 0.3);
          const e = u * u * (3 - 2 * u);
          x = ex + (tx - ex) * e;
          y = ey + (ty - ey) * e;
          scale = 1 - e * 0.3;
        }
        const delivered = u >= 1;
        token.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -50%) rotate(${angle.toFixed(1)}deg) scale(${(delivered ? 0.4 : scale).toFixed(3)})`;
        token.style.opacity = (wide ? t > 0.07 : u > 0) && !delivered ? "1" : "0";
        device.classList.toggle("is-delivered", delivered);
      }

      const y = window.scrollY;
      velocity = velocity * 0.7 + Math.abs(y - lastY) * 0.3;
      lastY = y;
      if (ticker) ticker.playbackRate = 1 + Math.min(velocity / 10, 3);
      // Let the ticker drift back to its resting speed after scrolling stops.
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        velocity = 0;
        if (ticker) ticker.playbackRate = 1;
      }, 180);
    };

    const onScroll = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.clearTimeout(settle);
      if (ticker) ticker.playbackRate = 1;
    };
  }, [rootRef, reduced]);
}

/** The hero picture leans a few degrees toward the cursor. */
function useTilt(ref) {
  const reduced = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced || !window.matchMedia("(pointer: fine)").matches) return undefined;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - (r.left + r.width / 2)) / window.innerWidth;
        const y = (e.clientY - (r.top + r.height / 2)) / window.innerHeight;
        el.style.setProperty("--rx", `${(-y * 5).toFixed(2)}deg`);
        el.style.setProperty("--ry", `${(x * 7).toFixed(2)}deg`);
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
    };
  }, [ref, reduced]);
}

/**
 * In-page links (#how, #faq ...) scroll smoothly to their section, below the
 * sticky header. Done by hand because the browser's own fragment scroll gets
 * cancelled by the page's other smooth scrolls (the gallery's autoplay).
 */
function useAnchorLinks(rootRef) {
  const reduced = useReducedMotion();
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"], a[href^="/#"]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const id = a.getAttribute("href").replace(/^\/?#/, "");
      const target = id && document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      // Wait two frames so a closing mobile menu has collapsed before measuring.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const top = target.getBoundingClientRect().top + window.scrollY - 76;
          window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
          window.history.replaceState(null, "", `#${id}`);
          // Images loading during the scroll can move the target; settle on it.
          let checks = 0;
          const settle = () => {
            const off = target.getBoundingClientRect().top - 76;
            if (Math.abs(off) > 3 && checks++ < 3) {
              window.scrollBy({ top: off, behavior: "smooth" });
              window.addEventListener("scrollend", settle, { once: true });
            }
          };
          window.addEventListener("scrollend", settle, { once: true });
        })
      );
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [rootRef, reduced]);
}

function useScrolled(offset = 24) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);
  return scrolled;
}

/**
 * Index that advances every `ms`, forever. Hovering never stops it; a real
 * interaction calls hold(), which only delays the next step by a few seconds.
 * Frozen under reduced motion.
 */
function useAutoplay(length, ms) {
  const [i, setI] = useState(0);
  const holdUntil = useRef(0);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return undefined;
    let id = 0;
    const tick = () => {
      const wait = holdUntil.current - Date.now();
      if (wait > 0) id = window.setTimeout(tick, wait);
      else setI((n) => (n + 1) % length);
    };
    id = window.setTimeout(tick, ms);
    return () => window.clearTimeout(id);
  }, [i, length, ms, reduced]);
  const hold = (extra = 6000) => {
    holdUntil.current = Date.now() + extra;
  };
  return [i, setI, hold];
}

/* ================================================================ shopify */

/** The "Built for Shopify" mark, as the App Store listing draws it. */
function BuiltForShopify() {
  return (
    <span className="pc-bfs">
      <svg viewBox="0 0 16 14" fill="none" aria-hidden="true">
        <path d="m13 0-1 5-4 9 8-9.5L13 0ZM3 0l1 5 4 9-8-9.5L3 0Z" fill="#1495CC" />
        <path d="m3 0 1 5 4 9 4-9 1-5H3Z" fill="#58B7DF" />
        <path d="M8 14 4 5l-4-.5L8 14ZM8 14l4-9 4-.5L8 14Z" fill="#035F86" />
        <path d="M8 5.5 4 5l4 9 4-9-4 .5Z" fill="#1495CC" />
        <path d="m4 5 4-5 4 5-4.001.5L4 5Z" fill="#A9DEF4" />
        <path d="M4 5 3 0h5L4 5ZM12 5l1-5H8l4 5Z" fill="#58B7DF" />
        <path d="M4 5 3 0 0 4.5 4 5ZM12 5l1-5 3 4.5-4 .5Z" fill="#1495CC" />
      </svg>
      Built for Shopify
    </span>
  );
}

/**
 * Shopify's official App Store badge, unmodified (shopify.dev brand assets).
 * Rules: link to the listing, keep it at least 30px tall, no recolouring or
 * effects, clear space of half its height around it.
 */
function AppStoreBadge({ light = false }) {
  return (
    <a
      className="pc-appstore"
      href={APP_STORE_URL}
      target="_blank"
      rel="noreferrer"
      aria-label="Find Pendora on the Shopify App Store"
    >
      <img
        src={light ? "/site-cream/shopify-app-store-badge-light.svg" : "/site-cream/shopify-app-store-badge.svg"}
        alt="Find it on the Shopify App Store"
        width="166"
        height="44"
      />
    </a>
  );
}

/* ================================================================ parts */

function Lines({ lines }) {
  // Each line rises out of its own mask.
  return lines.map((line, n) => (
    <span className="pc-line" key={n}>
      <span style={{ "--i": n }}>{line}</span>
    </span>
  ));
}

function Install({ className = "pc-btn--ink", children = "Install free on Shopify" }) {
  return (
    <a className={`pc-btn ${className}`} href={APP_STORE_URL} target="_blank" rel="noreferrer">
      {children}
      <IconArrow size={17} />
    </a>
  );
}

function Nav({ theme, onToggleTheme }) {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  return (
    <header className={`pc-nav${scrolled || open ? " is-scrolled" : ""}`}>
      <span className="pc-progress" aria-hidden="true" />
      <div className="pc-frame pc-nav__inner">
        <a className="pc-logo pc-logo--live" href="/" aria-label="Pendora Digital Downloads home">
          <img src={logo} alt="" width="28" height="28" />
          {/* the wordmark slides out from behind the logo tile, and every
              twelve seconds tucks back in and slides out again */}
          <span className="pc-logo__mask">
            <span className="pc-logo__word">
              Pendora <span className="pc-logo__sub">Digital Downloads</span>
            </span>
          </span>
        </a>
        <nav className="pc-nav__links" aria-label="Primary">
          {NAV.map((n) => (
            <a key={n.href} href={n.href}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="pc-nav__actions">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} className="pc-theme--bar" />
          <a className="pc-btn pc-btn--ink pc-btn--sm" href={APP_STORE_URL} target="_blank" rel="noreferrer">
            Install free
          </a>
          <button
            type="button"
            className="pc-nav__burger"
            aria-expanded={open}
            aria-controls="pc-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <IconClose size={20} /> : <IconMenu size={20} />}
          </button>
        </div>
      </div>
      <div id="pc-menu" className={`pc-nav__sheet${open ? " is-open" : ""}`} hidden={!open}>
        {NAV.map((n) => (
          <a key={n.href} href={n.href} onClick={() => setOpen(false)}>
            {n.label}
          </a>
        ))}
        <button type="button" className="pc-nav__themerow" onClick={onToggleTheme}>
          {theme === "night" ? "Light theme" : "Dark theme"}
          <span className="pc-theme" aria-hidden="true">
            <ThemeIcon />
          </span>
        </button>
        <Install />
      </div>
    </header>
  );
}

/**
 * Shopify's thank-you page (checkout extensibility look): store header,
 * confirmation, Pendora's app block, order summary. Not a real order.
 */
function ThankYouMock() {
  return (
    <div className="pc-mock pc-shop" aria-hidden="true">
      <div className="pc-shop__bar">
        <b>The Quiet Press</b>
        <span className="pc-shop__bag">
          <IconBag size={15} />
        </span>
      </div>
      <div className="pc-shop__body">
        <div className="pc-shop__hello">
          <span className="pc-shop__check">
            <IconCheck size={16} />
          </span>
          <div>
            <small>Confirmation #KQ4T7Z</small>
            <b>Thank you, Maya!</b>
          </div>
        </div>
        <div className="pc-shop__block">
          <div>
            <b>Your purchase has files available for download</b>
            <small>Delivered by Pendora</small>
          </div>
          <span className="pc-shop__btn">
            <IconDownload size={14} />
            Download
          </span>
        </div>
        <div className="pc-shop__card">
          <b>Your order is confirmed</b>
          <small>You&apos;ll receive a confirmation email with your order number shortly.</small>
        </div>
        <div className="pc-shop__summary">
          <span className="pc-mock__thumb">
            <em>1</em>
          </span>
          <span>
            <b>Field Notes, Vol. 1</b>
            <small>PDF + EPUB</small>
          </span>
          <b>$18.00</b>
        </div>
        <div className="pc-shop__foot">
          <span>Need help? Contact us</span>
          <span className="pc-shop__cta">Continue shopping</span>
        </div>
      </div>
    </div>
  );
}

function EmailMock() {
  return (
    <div className="pc-mock" aria-hidden="true">
      <div className="pc-mock__head">
        <span className="pc-mock__avatar">Q</span>
        <div>
          <b className="pc-mock__from">The Quiet Press</b>
          <small>to maya@studio.co · just now</small>
        </div>
      </div>
      <div className="pc-mail__body">
        <h4>Your download is ready</h4>
        <p>Hi Maya, thanks for your order. Your files for Field Notes, Vol. 1 are below.</p>
        <div className="pc-mail__files">
          <span>
            <i>PDF</i>field-notes-vol1.pdf
          </span>
          <span>
            <i className="is-key">KEY</i>FN1-7K2Q-9ZT4
          </span>
        </div>
        <span className="pc-mail__btn">Download your files</span>
      </div>
    </div>
  );
}

function PageMock() {
  return (
    <div className="pc-mock pc-mock--page" aria-hidden="true">
      <div className="pc-mock__head">
        <span className="pc-mock__check">
          <IconBag size={15} />
        </span>
        <div>
          <small>Order #1042 · maya@studio.co</small>
          <b>Your downloads</b>
        </div>
      </div>
      <div className="pc-mail__body">
        <div className="pc-mail__files">
          <span>
            <i>PDF</i>field-notes-vol1.pdf
            <em className="pc-mock__mini">
              <IconDownload size={13} />
            </em>
          </span>
          <span>
            <i className="is-epub">EPUB</i>field-notes-vol1.epub
            <em className="pc-mock__mini is-ghost">
              <IconDownload size={13} />
            </em>
          </span>
          <span>
            <i className="is-key">KEY</i>FN1-7K2Q-9ZT4
          </span>
        </div>
        <p className="pc-mock__fine">Downloads used: 1 of 5 · Link active for 47 more hours</p>
      </div>
    </div>
  );
}

const MOCKS = [ThankYouMock, EmailMock, PageMock];

/**
 * How it works: an Apple-style gallery. Swipe or scroll it sideways like any
 * native carousel; it also plays on its own until the visitor takes over.
 */
function HowGallery() {
  const rail = useRef(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const reduced = useReducedMotion();

  const goTo = (n) => {
    const el = rail.current;
    const card = el?.children[n];
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft - (el.clientWidth - card.clientWidth) / 2, behavior: "smooth" });
  };

  // Track which card is centred as the rail scrolls (by swipe, wheel or code).
  useEffect(() => {
    const el = rail.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const mid = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        [...el.children].forEach((c, n) => {
          const d = Math.abs(c.offsetLeft - el.offsetLeft + c.clientWidth / 2 - mid);
          const bd = Math.abs(el.children[best].offsetLeft - el.offsetLeft + el.children[best].clientWidth / 2 - mid);
          if (d < bd) best = n;
        });
        setActive(best);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Auto-play while the gallery is on screen. A swipe or click holds it for a
  // few seconds; only the pause button stops it.
  const holdUntil = useRef(0);
  useEffect(() => {
    if (!playing || reduced) return undefined;
    const el = rail.current;
    let visible = false;
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0.35 });
    if (el) io.observe(el);
    // Starting a sideways smooth scroll cancels any page scroll in progress
    // (in Chrome), so the gallery waits until the page has been still a moment.
    let lastPageScroll = 0;
    const onPageScroll = () => {
      lastPageScroll = Date.now();
    };
    window.addEventListener("scroll", onPageScroll, { passive: true });
    const id = window.setInterval(() => {
      const pageStill = Date.now() - lastPageScroll > 700;
      if (visible && pageStill && Date.now() > holdUntil.current) goTo((active + 1) % STEPS.length);
    }, 4800);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("scroll", onPageScroll);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, playing, reduced]);

  const hold = () => {
    holdUntil.current = Date.now() + 6000;
  };
  // Vertical trackpad scrolling over the gallery is page scrolling, not a swipe.
  const onWheel = (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold();
  };

  return (
    <section className="pc-section pc-how" id="how">
      <div className="pc-frame">
        <div className="pc-head pc-head--split" data-reveal>
          <div>
            <p className="pc-kicker">How it works</p>
            <h2>One order, three quiet moments.</h2>
          </div>
          <p>Nothing changes in your theme or checkout. Pendora listens for the paid order and does the rest, usually within a couple of seconds.</p>
        </div>
      </div>
      <ol className="pc-how__rail" ref={rail} onWheel={onWheel} onTouchStart={hold} onPointerDown={hold}>
        {STEPS.map((s, n) => {
          const Mock = MOCKS[n];
          return (
            <li key={s.title} className={`pc-how__card${n === active ? " is-on" : ""}`}>
              <div className="pc-how__stage">
                <Mock />
              </div>
              <div className="pc-how__text">
                <span className="pc-how__num">0{n + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <ul className="pc-notes">
                  {s.notes.map((t) => (
                    <li key={t}>
                      <IconCheck size={14} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="pc-how__controls">
        <div className="pc-pills" role="tablist" aria-label="Choose a step">
          {STEPS.map((s, n) => (
            <button
              key={s.title}
              type="button"
              role="tab"
              aria-selected={n === active}
              aria-label={s.title}
              className={n === active ? `is-on${playing ? " is-playing" : ""}` : undefined}
              onClick={() => {
                hold();
                goTo(n);
              }}
            >
              <i />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="pc-play"
          aria-label={playing ? "Pause" : "Play"}
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? (
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <rect x="2" y="1.5" width="2.6" height="9" rx="1" fill="currentColor" />
              <rect x="7.4" y="1.5" width="2.6" height="9" rx="1" fill="currentColor" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M3 1.8v8.4c0 .5.5.8.9.5l6.3-4.2c.4-.2.4-.8 0-1L3.9 1.3c-.4-.3-.9 0-.9.5Z" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>
    </section>
  );
}

const STATEMENT =
  "A customer pays. Before they close the tab, their files are on the thank-you page, in their inbox, and on a download page in your colours. That is the whole job, and Pendora does it for free.";

// Faint contour lines behind the statement; each layer drifts at its own
// speed as you scroll. Paths repeat every 1440 units so the drift can loop.
const WAVES = [
  "M0 150 C 240 110, 480 190, 720 150 S 1200 110, 1440 150 S 1920 190, 2160 150 S 2640 110, 2880 150",
  "M0 300 C 240 250, 480 350, 720 300 S 1200 250, 1440 300 S 1920 350, 2160 300 S 2640 250, 2880 300",
  "M0 470 C 240 520, 480 420, 720 470 S 1200 520, 1440 470 S 1920 420, 2160 470 S 2640 520, 2880 470",
  "M0 610 C 240 570, 480 650, 720 610 S 1200 570, 1440 610 S 1920 650, 2160 610 S 2640 570, 2880 610",
];

// The delivery thread. It travels the margins, above and below the words
// (never through them), then drops into the tablet in the next section.
const THREAD =
  "M -40 70 C 90 40, 190 60, 200 120 C 210 180, 120 190, 110 140 C 100 90, 190 70, 330 80 C 620 100, 980 40, 1240 90 C 1360 115, 1380 220, 1320 300 C 1270 370, 1330 470, 1250 540 C 1150 625, 900 600, 760 625 C 650 645, 720 720, 720 790";

/**
 * A statement that inks in word by word. Behind it, contour lines drift with
 * the scroll and a red thread draws itself, carrying a file along its tip
 * down into the tablet below.
 */
function Statement() {
  return (
    <section className="pc-statement" data-thread>
      <svg className="pc-waves" viewBox="0 0 1440 760" preserveAspectRatio="none" aria-hidden="true">
        {WAVES.map((d, n) => (
          <path key={n} d={d} className="pc-wave" style={{ "--speed": [0.12, -0.2, 0.28, -0.08][n] }} />
        ))}
      </svg>
      <svg className="pc-thread" viewBox="0 0 1440 760" preserveAspectRatio="none" aria-hidden="true">
        <path d={THREAD} pathLength="1" />
      </svg>
      <div className="pc-frame">
        <p className="pc-statement__text" data-words>
          {STATEMENT.split(" ").map((w, n) => (
            <span className="pc-word" key={n}>
              {w}{" "}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}

/**
 * The download page, shown on a large tablet that lies back at first and
 * settles flat as you scroll. It restyles itself through merchant palettes
 * and languages, like the real settings do.
 */
function DeviceReveal() {
  const [i] = useAutoplay(DEMO_PALETTES.length * TRY_LOCALES.length, 2800);
  const palette = DEMO_PALETTES[i % DEMO_PALETTES.length];
  const lang = TRY_LOCALES[i % TRY_LOCALES.length];
  return (
    <section className="pc-device" id="brand" data-device>
      <div className="pc-frame pc-device__head">
        <p className="pc-kicker">Your download page</p>
        <h2>Your brand, on every download.</h2>
        <p>
          Your colours, their language, every file and key in one place. Change any of it in
          the playground before you install.
        </p>
      </div>
      <div className="pc-device__stage">
        <div className="pc-device__body">
          <div className="pc-device__screen">
            <div className="pc-device__bar" aria-hidden="true">
              <span />
              <span />
              <span />
              <code>app.pendora.run/orders/1042</code>
              <em>
                <i style={{ background: palette.button }} />
                {palette.name} · {lang.toUpperCase()}
              </em>
            </div>
            <div className="pc-device__view">
              <div className="pc-device__wait" aria-hidden="true">
                <span className="pc-device__waitpill">
                  <span className="pc-device__spinner" />
                  Waiting for the order…
                </span>
              </div>
              <div className="lp pc-device__page" key={i} aria-hidden="true">
                <DownloadPageMock palette={palette} lang={lang} downloads={1} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="pc-device__cta">
        <a className="pc-btn pc-btn--ink" href={TRY_URL}>
          Try it yourself <IconArrow size={17} />
        </a>
        <span>A working copy of the app, right in your browser.</span>
      </div>
    </section>
  );
}

const FAN = [
  ...SELL,
  {
    img: "hero",
    title: "And everything else",
    text: "Templates, presets, game assets, fonts, tickets. If it is a file or a key, Pendora delivers it.",
    ext: "Any file type",
  },
];

/** What you can sell: a fan of cards. Click, swipe or drag through them. */
function CardFan() {
  const art = useArt();
  const [i, setI, hold] = useAutoplay(FAN.length, 3800);
  const drag = useRef(null);
  const go = (d) => {
    hold();
    setI((n) => (n + d + FAN.length) % FAN.length);
  };

  const onDown = (e) => {
    drag.current = { x: e.clientX, moved: false };
  };
  const onUp = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    drag.current = null;
  };

  const current = FAN[i];
  return (
    <div className="pc-fan">
      <div className="pc-fan__deck" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => (drag.current = null)}>
        {FAN.map((c, n) => {
          // Signed distance from the active card, wrapped so the fan is circular.
          let d = n - i;
          if (d > FAN.length / 2) d -= FAN.length;
          if (d < -FAN.length / 2) d += FAN.length;
          return (
            <button
              key={c.title}
              type="button"
              className={`pc-fan__card${d === 0 ? " is-on" : ""}`}
              style={{ "--d": d, "--a": Math.abs(d) }}
              onClick={() => {
                hold();
                setI(n);
              }}
              aria-label={c.title}
              tabIndex={d === 0 ? 0 : -1}
            >
              <img
                src={art[c.img]}
                srcSet={srcSet(art[c.img])}
                sizes="300px"
                alt=""
                width="1122"
                height="1402"
                draggable="false"
                loading="lazy"
              />
            </button>
          );
        })}
      </div>
      <div className="pc-fan__caption" aria-live="polite">
        <div key={current.title} className="pc-fan__copy">
          <h3>{current.title}</h3>
          <p>{current.text}</p>
          <small>{current.ext}</small>
        </div>
        <div className="pc-fan__nav">
          <button type="button" aria-label="Previous" onClick={() => go(-1)}>
            <IconArrow size={16} />
          </button>
          <span className="pc-fan__dots" aria-hidden="true">
            {FAN.map((c, n) => (
              <i key={c.title} className={n === i ? "is-on" : undefined} />
            ))}
          </span>
          <button type="button" aria-label="Next" onClick={() => go(1)}>
            <IconArrow size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <figure className="pc-rcard">
      <Stars size={13} />
      <blockquote>{r.quote}</blockquote>
      <figcaption>
        <b>{r.store}</b>
        {r.country}
      </figcaption>
    </figure>
  );
}

/**
 * Ease a row's marquee to a new speed (1 = normal, 0 = stopped) instead of
 * jumping, so a row glides to a halt under the cursor and drifts off again.
 */
function easeRowSpeed(row, target) {
  const anim = row?.getAnimations?.()[0];
  if (!anim) return;
  cancelAnimationFrame(row.__speedRaf);
  const from = anim.playbackRate;
  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - start) / 600);
    const e = 1 - Math.pow(1 - t, 3);
    anim.playbackRate = from + (target - from) * e;
    if (t < 1) row.__speedRaf = requestAnimationFrame(step);
  };
  row.__speedRaf = requestAnimationFrame(step);
}

/**
 * Two rows of reviews gliding in opposite directions, so several are always
 * readable at once. Each row is doubled so the loop is seamless. Hovering a
 * row (or focusing into it) brings it smoothly to a stop so the review can be
 * read; it picks up speed again when the cursor leaves.
 */
function ReviewRows() {
  const half = Math.ceil(REVIEWS.length / 2);
  const rows = [REVIEWS.slice(0, half), [...REVIEWS.slice(half), REVIEWS[0]]];
  return (
    <div className="pc-rrows" aria-label="Merchant reviews">
      {rows.map((row, n) => (
        <div
          key={n}
          className={`pc-rrow${n ? " pc-rrow--rev" : ""}`}
          onPointerEnter={(e) => easeRowSpeed(e.currentTarget, 0)}
          onPointerLeave={(e) => easeRowSpeed(e.currentTarget, 1)}
          onFocus={(e) => easeRowSpeed(e.currentTarget, 0)}
          onBlur={(e) => easeRowSpeed(e.currentTarget, 1)}
        >
          {[...row, ...row].map((r, k) => (
            <div key={`${r.store}-${k}`} aria-hidden={k >= row.length}>
              <ReviewCard r={r} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Plus() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/* ================================================================ page */

export default function HomePageCream({ theme: initialTheme = "cream" }) {
  const [theme, toggleTheme] = useSiteTheme(initialTheme);
  const art = ART[theme] || ART.cream;
  const root = useRef(null);
  useReveal(root);
  useAnchorLinks(root);
  useScrollFx(root);
  const heroArt = useRef(null);
  useTilt(heroArt);

  const tickerItems = [...TICKER, ...TICKER];

  return (
    <ThemeContext.Provider value={theme}>
    <div className={`pc${theme === "night" ? " pc--night" : ""}`} ref={root}>
      <a className="pc-skip" href="#main">
        Skip to content
      </a>

      <a className="pc-announce" href={TRY_URL}>
        New: try Pendora in your browser before you install <IconArrow size={13} />
      </a>

      <Nav theme={theme} onToggleTheme={toggleTheme} />

      {/* The file that travels from the statement into the tablet. */}
      <span className="pc-token" aria-hidden="true">
        <i>PDF</i>
        <b>field-notes.pdf</b>
      </span>

      <main id="main">
        {/* ------------------------------------------------ hero */}
        <section className="pc-hero">
          <div className="pc-frame pc-hero__grid">
            <div className="pc-hero__copy pc-hero__copy--load">
              <p className="pc-kicker">Digital downloads for Shopify</p>
              <h1>
                <Lines lines={["Every file,"]} />
                <em>
                  <Lines lines={["delivered beautifully."]} />
                </em>
              </h1>
              <p className="pc-hero__lede">
                Sell ebooks, music, courses, software and license keys. The moment an order is
                paid, Pendora puts a download button on the thank-you page and sends the files by
                email.
              </p>
              <div className="pc-hero__cta">
                <Install />
                <a className="pc-link" href={TRY_URL}>
                  Try it in your browser <IconArrow size={15} />
                </a>
              </div>
              <div className="pc-proof">
                <AppStoreBadge />
                <div className="pc-proof__meta">
                  <span className="pc-proof__rating">
                    <Stars size={13} />
                    <b>{RATING.value}</b> from {RATING.count} reviews
                  </span>
                  <span className="pc-proof__row">
                    <BuiltForShopify />
                    <span className="pc-proof__free">Free</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="pc-hero__visual pc-hero__visual--load">
              <div className="pc-hero__art" ref={heroArt}>
                <img
                  src={art.hero}
                  srcSet={srcSet(art.hero)}
                  sizes="(max-width: 960px) 92vw, 520px"
                  // React 18 passes this through only as a lowercase attribute.
                  {...{ fetchpriority: "high" }}
                  decoding="async"
                  alt="A cream ceramic box marked with the Pendora logo, opening as an ebook, a song, a video, an archive and a brass key float out"
                  width="1122"
                  height="1402"
                />
              </div>
              <div className="pc-float pc-float--a" aria-hidden="true">
                <span className="pc-float__icon pc-float__icon--ok">
                  <IconCheck size={16} />
                </span>
                <span>
                  <b>Order #1042 delivered</b>
                  <small>2 seconds after payment</small>
                </span>
              </div>
              <div className="pc-float pc-float--b" aria-hidden="true">
                <span className="pc-float__icon pc-float__icon--red">
                  <IconDownload size={16} />
                </span>
                <span>
                  <b>field-notes.pdf</b>
                  <small>Downloaded · 1 of 5</small>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------ ticker */}
        <div className="pc-ticker" aria-label="Things merchants sell with Pendora">
          <div className="pc-ticker__row">
            {tickerItems.map((t, n) => (
              <span key={`${t}-${n}`} aria-hidden={n >= TICKER.length}>
                {t}
              </span>
            ))}
          </div>
        </div>

        <Statement />

        <DeviceReveal />

        <HowGallery />

        {/* ------------------------------------------------ what you can sell */}
        <section className="pc-section" id="sell">
          <div className="pc-frame">
            <div className="pc-head" data-reveal>
              <p className="pc-kicker">What you can sell</p>
              <h2>If it&apos;s a file, it sells.</h2>
              <p>
                Link files and keys to a whole product or to a single variant, so every option
                delivers exactly the right thing.
              </p>
            </div>
            <CardFan />
          </div>
        </section>

        {/* ------------------------------------------------ features */}
        <section className="pc-section pc-section--paper" id="features">
          <div className="pc-frame">
            <div className="pc-head pc-head--split" data-reveal>
              <div>
                <p className="pc-kicker">Features</p>
                <h2>Everything delivery needs, included.</h2>
              </div>
              <p>No paid tiers and no watermark on your files. The features merchants usually pay extra for are simply part of the app.</p>
            </div>
            <ul
              className="pc-features"
              onPointerMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--sx", `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty("--sy", `${e.clientY - r.top}px`);
              }}
            >
              {FEATURES.map((f, n) => (
                <li key={f.title} data-reveal style={{ "--d": `${(n % 4) * 70}ms` }}>
                  <span className="pc-features__icon">
                    <f.Icon />
                  </span>
                  <b>{f.title}</b>
                  <span>{f.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ------------------------------------------------ reviews */}
        <section className="pc-section pc-section--tight" id="reviews">
          <div className="pc-frame">
            <div className="pc-rhead" data-reveal>
              <p className="pc-kicker">Reviews</p>
              <h2>Merchants, in their own words.</h2>
              <div className="pc-rstrip">
                <span className="pc-rstrip__score">
                  <b>{RATING.value}</b>
                  <Stars size={15} />
                </span>
                <span className="pc-rstrip__item">
                  <b>{RATING.count}</b> reviews
                </span>
                <span className="pc-rstrip__item">
                  <b>{RATING.fiveStarShare}</b> five stars
                </span>
                <a className="pc-rstrip__link" href={APP_STORE_REVIEWS_URL} target="_blank" rel="noreferrer">
                  Read them on the App Store <IconArrow size={14} />
                </a>
              </div>
            </div>
          </div>
          <ReviewRows />
        </section>

        {/* ------------------------------------------------ faq */}
        <section className="pc-section pc-section--paper" id="faq">
          <div className="pc-frame pc-faq">
            <div data-reveal>
              <p className="pc-kicker">FAQ</p>
              <h2>Questions, answered.</h2>
              <p className="pc-faq__aside">
                Something else? The team answers in live chat inside the app, usually within minutes.
              </p>
            </div>
            <div className="pc-faq__list" data-reveal style={{ "--d": "100ms" }}>
              {FAQ.map((f, n) => (
                <details key={f.q} open={n === 0}>
                  <summary>
                    {f.q}
                    <Plus />
                  </summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------ closing */}
        <section className="pc-section">
          <div className="pc-frame">
            <div className="pc-close" data-reveal>
              <div className="pc-close__text">
                <h2>
                  Open the box. <em>Start selling.</em>
                </h2>
                <p>
                  Install Pendora free, attach your first file and place a test order. Setup
                  takes minutes, and our team will check it with you.
                </p>
                <div className="pc-hero__cta">
                  <Install className="pc-btn--cream" />
                  <a className="pc-link pc-link--light" href={TRY_URL}>
                    Try it first <IconArrow size={15} />
                  </a>
                </div>
                <div className="pc-close__badges">
                  <AppStoreBadge />
                  <BuiltForShopify />
                </div>
              </div>
              <div className="pc-close__art" data-scroll>
                <img src={art.closing} srcSet={srcSet(art.closing)} sizes="(max-width: 900px) 92vw, 560px" alt="" width="1254" height="1254" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="pc-footer">
        <div className="pc-frame pc-footer__grid">
          <div className="pc-footer__brand">
            <a className="pc-logo" href="/" aria-label="Pendora Digital Downloads home">
              <img src={logo} alt="" width="28" height="28" />
              <span>
                Pendora <span className="pc-logo__sub">Digital Downloads</span>
              </span>
            </a>
            <p>Digital downloads and license keys for Shopify, delivered the moment an order is paid.</p>
            <div className="pc-footer__badges">
              <AppStoreBadge />
              <BuiltForShopify />
            </div>
          </div>
          <div>
            <p className="pc-footer__h">Product</p>
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
              Install on Shopify
            </a>
            <a href={TRY_URL}>Playground</a>
            <a href="#features">Features</a>
            <a href="#faq">FAQ</a>
          </div>
          <div>
            <p className="pc-footer__h">Company</p>
            <a href={PUMPER_URL} target="_blank" rel="noreferrer">
              Pumper, bundles for Shopify
            </a>
            <a href={PRIVACY_URL}>Privacy policy</a>
            <a href={DPA_URL}>Data processing addendum</a>
            <a href={TERMS_URL}>Terms of service</a>
            <a href={`${APP_STORE_URL}#adp-support`} target="_blank" rel="noreferrer">
              Support
            </a>
          </div>
        </div>
        <div className="pc-frame pc-footer__legal">
          <span>© {new Date().getFullYear()} Henqq ecommerce LLC</span>
          <span>Built for Shopify</span>
        </div>
        <span className="pc-wordmark" aria-hidden="true" data-reveal>
          {"Pendora".split("").map((ch, n) => (
            <span key={n} style={{ "--i": n }}>
              {ch}
            </span>
          ))}
        </span>
      </footer>
    </div>
    </ThemeContext.Provider>
  );
}
